import { useForecastAppContext } from "@Context/ForecastAppContext/ForecastAppContext";
import { useTranslations } from "@Context/TranslationContext/TranslationContext";
import RainIcon from "@Resources/images/iconsets/rain.svg?react";
import SunIcon from "@Resources/images/iconsets/sun.svg?react";
import SunriseIcon from "@Resources/images/iconsets/sunrise.svg?react";
import SunsetIcon from "@Resources/images/iconsets/sunset.svg?react";
import WindIcon from "@Resources/images/iconsets/wind.svg?react";

import {
  formatAsWeekday,
  getCurrentDay,
  getFormattedDate,
  getFormattedTimestring,
} from "@Utils/date";
import { formatRainProbability } from "@Utils/format";
import React from "react";

type WeatherSummaryItem = {
  icon: React.JSX.Element;
  description: string;
  a11yLabel: string;
  classNames?: string;
  testid?: string;
};

export const DailyWeatherOverview = () => {
  const { forecastPointWeek, activeDate } = useForecastAppContext();
  const { t, currentLocale } = useTranslations();

  const currentDay = getCurrentDay(forecastPointWeek, activeDate.value);
  const timezone = forecastPointWeek?.geolocation?.timezone ?? "Europe/Zurich";

  const weatherSummaryItems: WeatherSummaryItem[] = [
    {
      icon: (
        <RainIcon
          className="m:text-warmgrey-900 m:dark:text-blue-200"
          aria-hidden
        />
      ),
      description: `${currentDay.RRR_MM} mm / ${formatRainProbability(currentDay.PROBPCP_PERCENT)} %`,
      a11yLabel: t("weatherSummary.a11yRain", {
        RRR_MM: currentDay.RRR_MM,
        PROBPCP_PERCENT: formatRainProbability(currentDay.PROBPCP_PERCENT),
      }),
      testid: "daily-weather-overview-item-precipitation",
    },
    {
      icon: (
        <SunIcon
          className="m:text-warmgrey-900 m:dark:text-blue-200"
          aria-hidden
        />
      ),
      description: `${currentDay.SUN_H} h`,
      a11yLabel: t("weatherSummary.a11ySun", {
        SUN_H: currentDay.SUN_H,
      }),
      testid: "daily-weather-overview-item-sun",
    },
    {
      icon: (
        <WindIcon
          className="m:text-warmgrey-900 m:dark:text-blue-200"
          aria-hidden
        />
      ),
      description: `${currentDay.FF_KMH} | ${currentDay.FX_KMH} km/h`,
      classNames: "m:stroke-warmgrey-900 m:dark:stroke-blue-200 m:fill-none",
      a11yLabel: t("weatherSummary.a11yWind", {
        FF_KMH: currentDay.FF_KMH,
        FX_KMH: currentDay.FX_KMH,
      }),
      testid: "daily-weather-overview-item-wind",
    },
    {
      icon: (
        <SunriseIcon
          className="m:text-warmgrey-900 m:dark:text-blue-200"
          aria-hidden
        />
      ),
      description: getFormattedTimestring(
        currentDay.SUNRISE,
        timezone,
        currentLocale,
      ),
      a11yLabel: t("weatherSummary.a11ySunrise", {
        SUNRISE: getFormattedTimestring(
          currentDay.SUNRISE,
          timezone,
          currentLocale,
        ),
      }),
      testid: "daily-weather-overview-item-sunrise",
    },
    {
      icon: (
        <SunsetIcon
          className="m:text-warmgrey-900 m:dark:text-blue-200"
          aria-hidden
        />
      ),
      description: getFormattedTimestring(
        currentDay.SUNSET,
        timezone,
        currentLocale,
      ),
      a11yLabel: t("weatherSummary.a11ySunset", {
        SUNSET: getFormattedTimestring(
          currentDay.SUNSET,
          timezone,
          currentLocale,
        ),
      }),
      testid: "daily-weather-overview-item-sunset",
    },
  ];

  return (
    <>
      <section
        className="m:flex m:flex-row m:px-4 m:antialiased m:md:px-8 m:xl:px-10"
        aria-live="polite">
        <div
          className="m:md:flex m:md:flex-row"
          data-testid="daily-weather-overview">
          <div className="m:mr-8 m:flex m:max-w-12 m:flex-col m:font-medium m:break-normal!">
            <h3
              className="m:m-0 m:p-0 m:text-2xl m:leading-[20px] m:tracking-0.2 m:text-blue-950 m:lg:text-4xl m:lg:leading-[25px] m:dark:text-neutral-white"
              data-testid="daily-weather-overview-title">
              {formatAsWeekday(currentDay.date_time, timezone, currentLocale)}
            </h3>
            <span
              className="m:flex m:h-6 m:items-center m:text-base m:leading-[15px] m:tracking-0.4 m:text-blue-950 m:lg:mr-8 m:lg:text-xl m:lg:leading-[17px] m:lg:font-normal m:dark:text-neutral-white"
              data-testid="daily-weather-overview-date">
              {getFormattedDate(currentDay?.date_time, timezone, currentLocale)}
            </span>
          </div>
          {renderWeatherSummary(weatherSummaryItems.slice(0, 2))}
          {renderWeatherSummary(weatherSummaryItems.slice(2))}
        </div>
      </section>
    </>
  );
};

const renderWeatherSummary = (weatherSummaryItems: WeatherSummaryItem[]) => {
  return (
    <div className="m:mr-4 m:flex m:flex-row m:items-end m:gap-4 m:text-base m:font-normal m:antialiased m:lg:text-xl">
      {weatherSummaryItems.map((item: WeatherSummaryItem, index: number) => (
        <div
          className="m:flex m:flex-row m:items-center m:gap-2 m:first:w-[118px] m:md:first:w-auto"
          key={index}>
          {item.icon}
          <div
            className="m:text-warmgrey-900 m:dark:text-blue-200"
            aria-label={item.a11yLabel}
            tabIndex={-1}
            data-testid={item.testid ?? ""}>
            <span aria-hidden>{item.description}</span>
          </div>
        </div>
      ))}
    </div>
  );
};
